import { useEffect, useRef, useState } from 'react'
import chinaFlag from 'flag-icons/flags/1x1/cn.svg'
import denmarkFlag from 'flag-icons/flags/1x1/dk.svg'
import franceFlag from 'flag-icons/flags/1x1/fr.svg'
import germanyFlag from 'flag-icons/flags/1x1/de.svg'
import hungaryFlag from 'flag-icons/flags/1x1/hu.svg'
import indiaFlag from 'flag-icons/flags/1x1/in.svg'
import italyFlag from 'flag-icons/flags/1x1/it.svg'
import japanFlag from 'flag-icons/flags/1x1/jp.svg'
import norwayFlag from 'flag-icons/flags/1x1/no.svg'
import philippinesFlag from 'flag-icons/flags/1x1/ph.svg'
import polandFlag from 'flag-icons/flags/1x1/pl.svg'
import portugalFlag from 'flag-icons/flags/1x1/pt.svg'
import russiaFlag from 'flag-icons/flags/1x1/ru.svg'
import southKoreaFlag from 'flag-icons/flags/1x1/kr.svg'
import spainFlag from 'flag-icons/flags/1x1/es.svg'
import swedenFlag from 'flag-icons/flags/1x1/se.svg'
import taiwanFlag from 'flag-icons/flags/1x1/tw.svg'
import thailandFlag from 'flag-icons/flags/1x1/th.svg'
import turkeyFlag from 'flag-icons/flags/1x1/tr.svg'
import unitedNationsFlag from 'flag-icons/flags/1x1/un.svg'
import unitedStatesFlag from 'flag-icons/flags/1x1/us.svg'
import type {
  BrandConfiguration,
  LanguageOption,
  SignupStep,
} from './types'
import { useI18n } from './i18n'

const languageFlags: Record<string, { flag: string; short: string }> = {
  en: { flag: unitedStatesFlag, short: 'EN' },
  es: { flag: spainFlag, short: 'ES' },
  fr: { flag: franceFlag, short: 'FR' },
  de: { flag: germanyFlag, short: 'DE' },
  pt: { flag: portugalFlag, short: 'PT' },
  it: { flag: italyFlag, short: 'IT' },
  ru: { flag: russiaFlag, short: 'RU' },
  tr: { flag: turkeyFlag, short: 'TR' },
  ko: { flag: southKoreaFlag, short: 'KO' },
  ja: { flag: japanFlag, short: 'JA' },
  da: { flag: denmarkFlag, short: 'DA' },
  hu: { flag: hungaryFlag, short: 'HU' },
  no: { flag: norwayFlag, short: 'NO' },
  pl: { flag: polandFlag, short: 'PL' },
  sv: { flag: swedenFlag, short: 'SV' },
  zh: { flag: chinaFlag, short: 'ZH' },
  zh_tw: { flag: taiwanFlag, short: 'TW' },
  hi: { flag: indiaFlag, short: 'HI' },
  tl: { flag: philippinesFlag, short: 'TL' },
  th: { flag: thailandFlag, short: 'TH' },
}

function getLanguageAppearance(code: string) {
  const normalized = code.toLowerCase().replace('-', '_')
  return languageFlags[normalized] ?? {
    flag: unitedNationsFlag,
    short: normalized.slice(0, 2).toUpperCase(),
  }
}

interface BrandPanelProps {
  brand: BrandConfiguration
  termsUrl: string
}

export function BrandPanel({ brand, termsUrl }: BrandPanelProps) {
  const { t } = useI18n()

  return (
    <aside
      className="brand-panel"
      style={{ backgroundImage: `url("${brand.bannerImageUrl}")` }}
      aria-label={`${brand.name} branding`}
    >
      <div className="brand-tint" />
      <div className="brand-content">
        {brand.logoImageUrl ? (
          <img
            className="brand-logo"
            src={brand.logoImageUrl}
            alt={`${brand.name} logo`}
          />
        ) : (
          <div className="brand-name-fallback">{brand.name}</div>
        )}
        {brand.subTitle?.trim() && (
          <p className="brand-subtitle">{brand.subTitle.trim()}</p>
        )}
      </div>
      <div className="powered-by">
        <span>Powered by</span>
        <img src="/resources/img/idecide_white.svg" alt="iDecide" />
        <div>
          © All Things Interactive, LLC | USA ·{' '}
          <a href={termsUrl} target="_blank" rel="noreferrer">
            {t('signup.terms')}
          </a>
        </div>
      </div>
    </aside>
  )
}

const stepLabelKeys: Record<Exclude<SignupStep, 'done'>, string> = {
  bundle: 'signup.tl.bundle',
  purchase: 'signup.tl.purchase',
  account: 'signup.tl.account',
  'addons-pro': 'signup.tl.addons',
}

interface ProgressHeaderProps {
  currentStep: SignupStep
  addonsVisible: boolean
}

export function ProgressHeader({
  currentStep,
  addonsVisible,
}: ProgressHeaderProps) {
  const { t } = useI18n()
  const steps: Exclude<SignupStep, 'done'>[] = addonsVisible
    ? ['bundle', 'purchase', 'account', 'addons-pro']
    : ['bundle', 'purchase', 'account']
  const currentIndex =
    currentStep === 'done' ? steps.length : Math.max(0, steps.indexOf(currentStep as Exclude<SignupStep, 'done'>))
  const progress = Math.min(100, (currentIndex / Math.max(1, steps.length - 1)) * 100)

  return (
    <header className="progress-header">
      <div className="progress-track" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>
      <ol className="progress-steps" aria-label="Signup progress">
        {steps.map((step, index) => {
          const complete = index < currentIndex || currentStep === 'done'
          const current = index === currentIndex && currentStep !== 'done'

          return (
            <li
              key={step}
              className={`${complete ? 'complete' : ''} ${current ? 'current' : ''}`}
              aria-current={current ? 'step' : undefined}
            >
              <span className="step-marker">
                {complete ? '✓' : index + 1}
              </span>
              <span>{t(stepLabelKeys[step])}</span>
            </li>
          )
        })}
      </ol>
    </header>
  )
}

interface NavigationDockProps {
  currentStep: SignupStep
  canContinue: boolean
  languages: LanguageOption[]
  languageCode: string
  continueLabel: string
  onBack: () => void
  onLanguageChange: (languageCode: string) => void
  allowBack?: boolean
}

export function NavigationDock({
  currentStep,
  canContinue,
  languages,
  languageCode,
  continueLabel,
  onBack,
  onLanguageChange,
  allowBack = true,
}: NavigationDockProps) {
  const { t } = useI18n()
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false)
  const languageDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const closeLanguageMenu = (event: MouseEvent) => {
      if (!languageDropdownRef.current?.contains(event.target as Node))
        setLanguageMenuOpen(false)
    }

    document.addEventListener('mousedown', closeLanguageMenu)
    return () => document.removeEventListener('mousedown', closeLanguageMenu)
  }, [])

  if (currentStep === 'done')
    return null

  const selectedLanguage =
    languages.find((language) => language.code === languageCode) ?? languages[0]
  const selectedAppearance = getLanguageAppearance(
    selectedLanguage?.code ?? languageCode,
  )

  return (
    <footer className="navigation-dock">
      <div className="language-dropdown" ref={languageDropdownRef}>
        <button
          className="language-button"
          type="button"
          aria-haspopup="menu"
          aria-expanded={languageMenuOpen}
          aria-controls="signup-language-menu"
          aria-label={`Language: ${selectedLanguage?.name ?? selectedLanguage?.englishName ?? languageCode}`}
          onClick={() => setLanguageMenuOpen((open) => !open)}
        >
          <img
            className="language-flag"
            src={selectedAppearance.flag}
            alt=""
            aria-hidden="true"
          />
          <span>{selectedAppearance.short}</span>
          <span className="language-chevron" aria-hidden="true">⌄</span>
        </button>
        {languageMenuOpen && (
          <div
            className="language-menu"
            id="signup-language-menu"
            role="menu"
            aria-label="Choose a language"
          >
            {languages.map((language) => {
              const appearance = getLanguageAppearance(language.code)
              const selected = language.code === languageCode

              return (
                <button
                  key={language.code}
                  className={`language-option ${selected ? 'selected' : ''}`}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  title={language.name || language.englishName}
                  onClick={() => {
                    setLanguageMenuOpen(false)
                    onLanguageChange(language.code)
                  }}
                >
                  <img
                    className="language-flag"
                    src={appearance.flag}
                    alt=""
                    aria-hidden="true"
                  />
                  <span>{appearance.short}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>
      <div className="navigation-actions">
        {currentStep !== 'bundle' && allowBack && (
          <button className="button button-link" type="button" onClick={onBack}>
            ← {t('common.back')}
          </button>
        )}
        <button
          className="button button-primary"
          type="submit"
          disabled={!canContinue}
        >
          {continueLabel}
        </button>
      </div>
    </footer>
  )
}

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  autoComplete?: string
  required?: boolean
  prefix?: string
  suffix?: string
  hint?: string
  full?: boolean
  readOnly?: boolean
  maxLength?: number
  error?: string
}

export function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  required,
  prefix,
  suffix,
  hint,
  full,
  readOnly,
  maxLength,
  error,
}: FieldProps) {
  const { t } = useI18n()
  const describedBy = [hint ? `${id}-hint` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ') || undefined

  return (
    <label
      className={`field ${full ? 'field-full' : ''} ${error ? 'field-error' : ''}`}
      htmlFor={id}
    >
      <span className="field-label">
        {label}
        {required && (
          <span className="required-mark" aria-hidden="true">*</span>
        )}
        {!required && <span className="optional"> {t('signup.optional')}</span>}
      </span>
      <span className={`input-shell ${readOnly ? 'read-only' : ''}`}>
        {prefix && <span className="input-affix">{prefix}</span>}
        <input
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          readOnly={readOnly}
          maxLength={maxLength}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix && <span className="input-affix">{suffix}</span>}
      </span>
      {hint && <span className="field-hint" id={`${id}-hint`}>{hint}</span>}
      {error && (
        <span className="field-error-message" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </label>
  )
}

export function SectionHeading({
  children,
  eyebrow,
  centered,
}: {
  children: React.ReactNode
  eyebrow?: string
  centered?: boolean
}) {
  return (
    <div className={centered ? 'heading centered' : 'heading'}>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{children}</h1>
    </div>
  )
}
